import type { BlogPost } from "./types";

// Image guidance from source doc (photo omitted for now; add to public/blog/ later):
//  *   Person using mobile phone to shop on accessible e-commerce website — alt: "Person using a mobile phone to browse an accessible e-commerce website showing product images with alt text, clear navigation, and a high-contrast Add to Cart button"
//  *   Accessible product listing page with labelled images and clear CTA buttons — alt: "Accessible e-commerce product page showing a product image with descriptive alt text, price in high-contrast text, accessible Add to Cart button with clear focus indicator, and visible product description"
const post: BlogPost = {
  slug: "d2c-ecommerce-accessibility-guide-india",
  title: "Accessibility for D2C and E-commerce Brands",
  metaTitle: "D2C Accessibility Guide for Indian Brands",
  metaDescription:
    "Inaccessible e-commerce sites lose customers with disabilities. A practical guide to fixing the most common D2C accessibility failures.",
  subtitle:
    "The accessibility failures that cost online stores customers, and the quick fixes that deal with most of them.",
  date: "2026-07-17",
  photo: "/blog/d2c-ecommerce-accessibility-guide-india.svg",
  alt:
    "Person using a mobile phone to browse an accessible e-commerce website showing product images with alt text, clear navigation, and a high-contrast Add to Cart button",
  author: "AccessBharat",
  tag: "D2C",
  excerpt:
    "The accessibility failures that cost online stores customers, and the quick fixes that deal with most of them.",
  body: `Direct-to-consumer brands put a lot into user experience: fast load times, smooth checkout, personalisation, mobile-first design. Many have also, without meaning to, excluded a large group of customers from all of it: the estimated 15% of the world's population who live with some form of disability.

For an online store, inaccessibility means lost sales. A customer reaches your homepage, cannot find their way around the catalogue, and leaves. Another fills a cart and abandons it because the checkout form does not work with a screen reader.

## What accessibility covers in e-commerce

Different customers need different things: people with low vision rely on high contrast and zoom, people with motor impairments navigate by keyboard, people with cognitive disabilities need clear layouts, and people with hearing loss need captions on video.

The elements that fail most often on e-commerce sites are:

- product images without alt text
- icon-only buttons with no labels
- pop-ups that trap keyboard focus
- carousels that auto-play and cannot be paused
- sale badges that rely on colour alone
- forms that show an error without saying what to fix

## The legal picture

In the United States, ADA Title III lawsuits against retailers over their websites number in the thousands each year. In Europe, the EAA has applied since June 2025 to brands selling into the EU, and conformance is generally shown against EN 301 549, which is based on WCAG 2.1 AA. In India, the RPwD Act applies to any business serving the public.

If you plan to sell abroad, put accessibility on the same expansion checklist as payments and language support.

## Quick wins

You do not need a redesign to start. Begin with:

- descriptive alt text on every product image
- body text contrast of at least 4.5:1
- 'Add to Cart' and 'Buy Now' buttons that work from the keyboard
- captions on product videos
- clear error messages on every form field

These take hours, not months, and they make the store easier for everyone to use.

## Brand value

Many shoppers, younger ones in particular, care about what the brands they buy from stand for. Accessible design shows that a brand thinks about all its customers, which counts for something when you are building loyalty and community.
`,
};

export default post;
